import { createDate } from 'core/utils/date'

import { type LoadDimensions } from '../domain/LoadDimensions'
import { type LoadIntermodal } from '../domain/LoadIntermodal'
import { type ActiveOffer, type LoadOffer } from '../domain/LoadOffer'
import { type LoadRequirements } from '../domain/LoadRequirements'
import { type LoadStop } from '../domain/LoadStop'
import { type OfferBid } from '../domain/OfferBid'
import { type OfferContract } from '../domain/OfferContract'
import { type OfferEligibility } from '../domain/OfferEligibility'
import {
  mapContract,
  mapDimensions,
  mapIntermodal,
  mapLastBid,
  mapRequirements,
  mapStop,
  type RawLoad,
  type RawOffer,
} from './offerMapper'

export type RawLoadOffer = RawLoad & {
  active_offer?: Omit<RawOffer, 'load'>
}

export function mapApiToLoadOffer(rawLoadOffer: RawLoadOffer): LoadOffer {
  const intermodal: LoadIntermodal | undefined = mapIntermodal(rawLoadOffer.intermodal)

  const stops: Array<LoadStop> = rawLoadOffer.stops.map(stop => mapStop(stop))

  const dimensions: LoadDimensions = mapDimensions(rawLoadOffer.dimensions)

  const requirements: LoadRequirements | undefined = mapRequirements(rawLoadOffer.requirements)

  let activeOffer: ActiveOffer | undefined
  if (rawLoadOffer.active_offer) {
    const contract: OfferContract | undefined = mapContract(rawLoadOffer.active_offer.contract)

    const eligibility: OfferEligibility = {
      eligible: rawLoadOffer.active_offer.eligibility.eligible,
      reasons: rawLoadOffer.active_offer.eligibility.reasons ?? undefined,
    }

    const lastBid: OfferBid | undefined = mapLastBid(rawLoadOffer.active_offer.last_bid)

    activeOffer = {
      biddable: rawLoadOffer.active_offer.biddable,
      contract,
      createdAt: new Date(rawLoadOffer.active_offer.created_at),
      eligibility,
      expiresAt: createDate(rawLoadOffer.active_offer.expires_at),
      lastBid,
      offeredByName: rawLoadOffer.active_offer.offered_by_name ?? undefined,
      price: rawLoadOffer.active_offer.price,
      pricingMethod: rawLoadOffer.active_offer.pricing_method,
      uuid: rawLoadOffer.active_offer.uuid,
      daimlerVip: rawLoadOffer.active_offer.daimler_vip,
    }
  }

  const loadOffer: LoadOffer = {
    activeOffer: activeOffer ?? undefined,
    commodity: rawLoadOffer.commodity ?? undefined,
    createdAt: new Date(rawLoadOffer.created_at),
    customerRequirements: rawLoadOffer.customer_requirements,
    dimensions,
    directFromShipper: rawLoadOffer.direct_from_shipper,
    equipmentSubtypes: rawLoadOffer.equipment_subtypes ?? undefined,
    equipmentType: rawLoadOffer.equipment_type,
    intermodal,
    mileage: rawLoadOffer.mileage,
    refNumber: rawLoadOffer.ref_number,
    requirements,
    shipperId: rawLoadOffer.shipper_id,
    shipperMarket: rawLoadOffer.shipper_market ?? undefined,
    stops,
    tags: rawLoadOffer.tags ?? undefined,
    teamDriver: Boolean(rawLoadOffer.team_driver),
    temperature: rawLoadOffer.temperature ?? undefined,
    uuid: rawLoadOffer.uuid,
    weight: rawLoadOffer.weight,
    mode: rawLoadOffer.mode,
  }

  return loadOffer
}
